// ──────────────────────────────────────────────────────────────────────
// Weekly Social Schedule widget — consumer of the Fretless design system.
//
//   Card → WidgetHeader → (empty | list of ListRow)
//
// Mirrors the Upcoming Gigs structure: DateBlock leading, artist@venue
// primary, suggested-post secondary, mark-as-posted Check trailing.
// ──────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Check, Eye } from "lucide-react";
import { format, parseISO, addDays, differenceInCalendarDays } from "date-fns";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { DragHandle } from "@/hooks/useDragHandle";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import {
  Card,
  Band,
  Kicker,
  WidgetHeader,
  ListRow,
} from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

const { color, font, space, radius, border } = tokens;

const POST_TYPES = [
  { key: "announcement", daysBefore: [10, 9, 8, 7], emoji: "📢", suggestedLabel: "Announcement post" },
  { key: "reminder",     daysBefore: [3],           emoji: "🔔", suggestedLabel: "Reminder post" },
  { key: "tonight",      daysBefore: [0],           emoji: "⚡", suggestedLabel: "Tonight post" },
  { key: "recap",        daysBefore: [-1],          emoji: "📸", suggestedLabel: "Recap post" },
];

// ───── Date block (leading slot for each ListRow) ────────────────────
function DateBlock({ postDate, isToday, compact = false }) {
  const width = compact ? 44 : 58;
  const numeralSize = compact ? 20 : 24;
  return (
    <div
      style={{
        width,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Kicker size={11} color={isToday ? color.orange : color.textDim}>
        {isToday ? "TODAY" : format(postDate, "EEE").toUpperCase()}
      </Kicker>
      <div
        style={{
          fontFamily: font.body,
          fontWeight: 600,
          fontSize: numeralSize,
          lineHeight: 1,
          color: color.text,
          letterSpacing: "-0.02em",
        }}
      >
        {format(postDate, "d")}
      </div>
      <Kicker size={11} color={color.textDim}>
        {format(postDate, "MMM").toUpperCase()}
      </Kicker>
    </div>
  );
}

// ───── Mark-as-posted Check button (trailing slot) ───────────────────
function MarkPostedButton({ gigId, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(gigId);
      }}
      title="Mark as posted"
      aria-label="Mark as posted"
      data-testid={`social-schedule-mark-posted-${gigId}`}
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: color.green,
        border: "none",
        borderRadius: radius.md,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <Check style={{ width: 14, height: 14 }} />
    </button>
  );
}

// ───── Hide button (matches RevenueOverview / UpcomingGigs) ──────────
function HideButton({ onHide }) {
  return (
    <button
      type="button"
      onClick={onHide}
      aria-label="Hide widget"
      title="Hide widget"
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: color.textMuted,
        border: "none",
        borderRadius: radius.md,
        cursor: "pointer",
      }}
    >
      <Eye className="h-4 w-4" />
    </button>
  );
}

// ───── Empty state ───────────────────────────────────────────────────
function EmptyState() {
  return (
    <Band last padding={`${space.lg}px ${space.xl}px ${space.xl}px`}>
      <p
        style={{
          fontFamily: font.body,
          fontSize: 13,
          color: color.textMuted,
          margin: 0,
        }}
      >
        No upcoming posts
      </p>
    </Band>
  );
}

// ───── Public component ─────────────────────────────────────────────
export const WeeklySocialSchedule = ({ gigs = [], navigate, onHide }) => {
  // Server-backed set of gig_ids the user has marked as posted (dismissed).
  // Hydrated once on mount so refreshes / device-switches stay in sync.
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/social/posts/dismissed");
        if (!cancelled) setDismissedIds(new Set(res.data?.gig_ids || []));
      } catch {
        // Silent — empty set is a safe default.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build full schedule of upcoming post suggestions across all future gigs.
  const schedule = [];
  for (const gig of gigs) {
    if (!gig.date || gig.is_public === false) continue;
    const gigDate = parseISO(gig.date);
    for (const postType of POST_TYPES) {
      for (const daysBefore of postType.daysBefore) {
        const postDate = addDays(gigDate, -daysBefore);
        if (postDate >= today) {
          schedule.push({
            postDate,
            postType: postType.key,
            emoji: postType.emoji,
            suggestedLabel: postType.suggestedLabel,
            gig,
            gigDate,
          });
        }
      }
    }
  }
  schedule.sort((a, b) => a.postDate - b.postDate);

  // Dedupe by gig_id — one row per gig, keep earliest-due post, drop dismissed.
  const seenGigIds = new Set();
  const dedupedSchedule = [];
  for (const item of schedule) {
    const gid = item.gig?.gig_id;
    if (!gid || seenGigIds.has(gid) || dismissedIds.has(gid)) continue;
    seenGigIds.add(gid);
    dedupedSchedule.push(item);
  }

  const markAsPosted = async (gigId) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(gigId);
      return next;
    });
    try {
      await apiClient.post(`/social/reminders/${gigId}/dismiss`);
    } catch {
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.delete(gigId);
        return next;
      });
      toast.error("Couldn't mark as posted — try again");
      return;
    }
    toast.success("Marked as posted", {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            await apiClient.delete(`/social/reminders/${gigId}/dismiss`);
            setDismissedIds((prev) => {
              const next = new Set(prev);
              next.delete(gigId);
              return next;
            });
          } catch {
            toast.error("Undo failed");
          }
        },
      },
    });
  };

  const isEmpty = dedupedSchedule.length === 0;
  const isDesktop = useIsDesktop(900);
  const compact = !isDesktop;
  const headerPadding = compact ? `${space.md}px ${space.md}px` : undefined;
  const bandPadding = compact
    ? `${space.sm}px ${space.md}px ${space.md}px`
    : `${space.md}px ${space.xl}px ${space.xl}px`;
  const rowStyle = compact
    ? { padding: `${space.sm}px ${space.md}px`, gap: space.md }
    : undefined;

  return (
    <Card
      maxWidth="none"
      style={{ height: "100%" }}
      data-testid={isEmpty ? "weekly-social-empty" : "weekly-social-schedule"}
    >
      <WidgetHeader
        title="Social schedule"
        leading={<DragHandle />}
        padding={headerPadding}
        actions={onHide ? <HideButton onHide={onHide} /> : null}
      />
      {isEmpty ? (
        <EmptyState />
      ) : (
        <Band last padding={bandPadding}>
          <div style={{ display: "flex", flexDirection: "column", gap: space.sm }}>
            {dedupedSchedule.slice(0, 5).map((item) => {
              const isToday = differenceInCalendarDays(item.postDate, today) === 0;
              const gigId = item.gig.gig_id;
              const secondary = (
                <span>
                  <span style={{ marginRight: 4 }} aria-hidden="true">
                    {item.emoji}
                  </span>
                  <span style={{ color: color.textMuted }}>Suggested: </span>
                  <span style={{ color: color.textDim }}>{item.suggestedLabel}</span>
                </span>
              );
              return (
                <ListRow
                  key={gigId}
                  data-testid={`social-schedule-${item.postType}`}
                  style={rowStyle}
                  leading={<DateBlock postDate={item.postDate} isToday={isToday} compact={compact} />}
                  primary={`${item.gig.artist_name} @ ${item.gig.venue_name}`}
                  secondary={secondary}
                  trailing={<MarkPostedButton gigId={gigId} onClick={markAsPosted} />}
                  onClick={() => navigate(`/social?gig_id=${gigId}`)}
                />
              );
            })}
          </div>
        </Band>
      )}
    </Card>
  );
};

export default WeeklySocialSchedule;
